import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShieldCheck, AlertTriangle, BookOpen, Home, ChevronDown, FileText, HelpCircle, Globe, Calendar, Info, LayoutTemplate } from 'lucide-react';
import { Button } from './Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';
export function Header() {
  const {
    t,
    language
  } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState<string | null>(null);
  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileExpandedSection(null);
    setDesktopDropdownOpen(null);
  }, [location.pathname]);
  const navStructure = [{
    key: 'nav.home',
    path: `/${language}`,
    icon: Home,
    type: 'link'
  }, {
    key: 'nav.tools',
    icon: ShieldCheck,
    type: 'dropdown',
    children: [{
      key: 'nav.checkScam',
      path: `/${language}/check-scam`,
      icon: ShieldCheck
    }, {
      key: 'nav.cyberQuiz',
      path: `/${language}/cyber-quiz`,
      icon: HelpCircle
    }, {
      key: 'nav.reportTemplate',
      path: `/${language}/report-template-generator`,
      icon: LayoutTemplate
    }]
  }, {
    key: 'nav.emergency',
    icon: AlertTriangle,
    type: 'dropdown',
    children: [{
      key: 'nav.emergency',
      path: `/${language}/emergency-protocol`,
      icon: AlertTriangle
    }, {
      key: 'nav.whereToReport',
      path: `/${language}/where-to-report`,
      icon: FileText
    }, {
      key: 'nav.evidenceSafety',
      path: `/${language}/evidence-digital-safety`,
      icon: ShieldCheck
    }]
  }, {
    key: 'nav.education',
    labelKey: 'footer.learn',
    icon: BookOpen,
    type: 'dropdown',
    children: [{
      key: 'nav.educationCenter',
      path: `/${language}/education`,
      icon: BookOpen
    }, {
      key: 'nav.internationalFramework',
      path: `/${language}/international-framework`,
      icon: Globe
    }, {
      key: 'nav.events',
      path: `/${language}/events`,
      icon: Calendar
    }]
  }, {
    key: 'nav.about',
    path: `/${language}/about`,
    icon: Info,
    type: 'link'
  }];
  const isActive = (path: string) => {
    if (path === `/${language}` && location.pathname === `/${language}`) {
      return true;
    }
    if (path !== `/${language}` && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };
  const isSectionActive = (children: any[]) => {
    return children.some(child => isActive(child.path));
  };
  const toggleMobileSection = (key: string) => {
    setMobileExpandedSection(mobileExpandedSection === key ? null : key);
  };
  return <>
      {/* Sub-header Banner - Statistics */}
      <div className={`fixed top-0 left-0 right-0 z-50 bg-accent-600 text-white transition-all duration-300 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'}`} data-id="element-138">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 sm:py-2" data-id="element-139">
          <p className="text-center text-xs sm:text-sm font-medium" data-id="element-140">
            <span className="font-bold text-white" data-id="element-141">
              63% of people in Southeast Asia
            </span>
            <span className="text-red-100" data-id="element-142">
              {' '}
              experienced online scams last year.{' '}
            </span>
            <span className="hidden sm:inline text-white font-semibold" data-id="element-143">
              Don't be the next victim.
            </span>
            <span className="sm:hidden text-white font-semibold" data-id="element-144">
              Stay safe.
            </span>
          </p>
        </div>
      </div>

      <header key={`header-${language}`} className={`fixed left-0 right-0 z-50 transition-all duration-300 bg-primary-900 ${scrolled ? 'top-0 shadow-lg border-b border-accent-600/50 py-1' : 'top-8 sm:top-9 border-b border-white/5 py-2'}`} data-id="element-145">
        {/* Top accent line - Red */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-900 via-accent-600 to-primary-900 opacity-80" data-id="element-146"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-147">
          <div className="flex items-center justify-between h-16" data-id="element-148">
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" key={`nav-${language}`} data-id="element-149">
              {navStructure.map(item => {
              if (item.type === 'link') {
                return <Button key={`${item.key}-${language}`} variant="ghost" size="sm" href={item.path} className={`text-sm font-medium transition-colors duration-200 ${isActive(item.path!) ? 'bg-white/10 text-white font-semibold border-b-2 border-accent-500 rounded-b-none shadow-sm' : 'text-slate-100 hover:text-white hover:bg-white/10'}`} data-id="element-150">
                      {t(item.key)}
                    </Button>;
              }
              // Dropdown Menu
              const active = isSectionActive(item.children!);
              const isOpen = desktopDropdownOpen === item.key;
              return <div key={`${item.key}-${language}`} className="relative group" onMouseEnter={() => setDesktopDropdownOpen(item.key)} onMouseLeave={() => setDesktopDropdownOpen(null)} data-id="element-151">
                    <button className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none ${active || isOpen ? 'bg-white/10 text-white' : 'text-slate-100 hover:text-white hover:bg-white/10'}`} aria-expanded={isOpen} data-id="element-152">
                      {item.labelKey ? t(item.labelKey) : t(item.key)}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-153" />
                    </button>

                    {/* Dropdown Content */}
                    <div className={`absolute left-0 mt-0 w-64 bg-primary-800 rounded-md shadow-xl border border-white/10 overflow-hidden transition-all duration-200 origin-top-left z-50 ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`} data-id="element-154">
                      <div className="py-1" data-id="element-155">
                        {item.children!.map(child => {
                      const ChildIcon = child.icon;
                      const isChildActive = isActive(child.path);
                      return <Link key={`${child.key}-${language}`} to={child.path} className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isChildActive ? 'bg-white/10 text-white border-l-2 border-accent-500' : 'text-slate-200 hover:bg-white/5 hover:text-white'}`} data-id="element-156">
                              <ChildIcon className={`h-4 w-4 ${isChildActive ? 'text-accent-400' : 'text-slate-400'}`} data-id="element-157" />
                              {t(child.key)}
                            </Link>;
                    })}
                      </div>
                    </div>
                  </div>;
            })}
            </nav>

            {/* Mobile: Show hamburger on left */}
            <div className="lg:hidden" data-id="element-158">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-md text-slate-100 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/50" aria-expanded={isMobileMenuOpen} aria-label="Toggle navigation menu" data-id="element-159">
                {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" data-id="element-160" /> : <Menu className="h-6 w-6" aria-hidden="true" data-id="element-161" />}
              </button>
            </div>

            {/* Right Section: Language Switcher & Logo */}
            <div className="flex items-center gap-4" data-id="element-162">
              <div className="hidden sm:block" data-id="element-163">
                <LanguageSwitcher data-id="element-164" />
              </div>

              {/* Logo Section */}
              <Link to={`/${language}`} className="flex items-center focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-1" aria-label="genZAFE.org Home" data-id="element-165">
                <img src="https://cdn.magicpatterns.com/uploads/uXx3yCsHjduce19DFGonX9/yseali-cybersafe-logo-desktop.png" alt="genZAFE.org" className="h-11 w-auto" data-id="element-166" />
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-16 left-0 right-0 border-b border-accent-600/30 shadow-lg animate-slide-up max-h-[calc(100vh-4rem)] overflow-y-auto bg-primary-900" data-id="element-167">
            <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-168">
              <div className="flex items-center justify-between px-2 py-3 mb-2 border-b border-white/10 sm:hidden" data-id="element-169">
                <span className="text-sm font-medium text-slate-100" data-id="element-170">
                  Language
                </span>
                <LanguageSwitcher data-id="element-171" />
              </div>

              {navStructure.map(item => {
            if (item.type === 'link') {
              const Icon = item.icon;
              const active = isActive(item.path!);
              return <Link key={`mobile-${item.key}-${language}`} to={item.path!} className={`flex items-center gap-3 px-4 py-4 rounded-lg text-base font-medium transition-colors ${active ? 'bg-white/10 text-white border-l-4 border-accent-500' : 'text-slate-100 hover:bg-white/5 hover:text-white'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-172">
                      <Icon className={`h-5 w-5 ${active ? 'text-accent-400' : 'text-slate-300'}`} data-id="element-173" />
                      {t(item.key)}
                    </Link>;
            }
            // Mobile Accordion for Dropdowns
            const isExpanded = mobileExpandedSection === item.key;
            const active = isSectionActive(item.children!);
            const Icon = item.icon;
            return <div key={`mobile-group-${item.key}-${language}`} className="rounded-lg overflow-hidden" data-id="element-174">
                    <button onClick={() => toggleMobileSection(item.key)} className={`w-full flex items-center justify-between px-4 py-4 text-base font-medium transition-colors ${active ? 'text-white bg-white/5' : 'text-slate-100 hover:bg-white/5 hover:text-white'}`} data-id="element-175">
                      <div className="flex items-center gap-3" data-id="element-176">
                        <Icon className={`h-5 w-5 ${active ? 'text-accent-400' : 'text-slate-300'}`} data-id="element-177" />
                        {item.labelKey ? t(item.labelKey) : t(item.key)}
                      </div>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-178" />
                    </button>

                    {isExpanded && <div className="bg-black/20 py-1" data-id="element-179">
                        {item.children!.map(child => {
                  const ChildIcon = child.icon;
                  const isChildActive = isActive(child.path);
                  return <Link key={`mobile-child-${child.key}-${language}`} to={child.path} className={`flex items-center gap-3 pl-12 pr-4 py-3 text-sm font-medium transition-colors ${isChildActive ? 'text-accent-400' : 'text-slate-300 hover:text-white'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-180">
                              <ChildIcon className="h-4 w-4" data-id="element-181" />
                              {t(child.key)}
                            </Link>;
                })}
                      </div>}
                  </div>;
          })}

              {/* Mobile CTA */}
              <div className="pt-4 px-2" data-id="element-182">
                <Button variant="destructive" fullWidth size="lg" href={`/${language}/emergency-protocol`} onClick={() => setIsMobileMenuOpen(false)} leftIcon={<AlertTriangle className="h-5 w-5" data-id="element-184" />} data-id="element-183">
                  {t('nav.emergency')}
                </Button>
              </div>
            </div>
          </div>}
      </header>
    </>;
}